import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Lightbulb } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  dismissGuidance,
  listGuidanceForRecipient,
  markGuidanceRead,
  resolveGuidanceRoute,
  type GuidanceMessage,
} from "@/lib/admin/guidance";

/** Visar guidning från supporten med en direktlänk till nästa steg. */
const GuidanceInbox: React.FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<GuidanceMessage[]>([]);

  const load = useCallback(async (uid: string) => {
    try {
      const rows = await listGuidanceForRecipient(uid);
      setMessages(rows);
      await Promise.all(rows.filter((m) => !m.read_at).map((m) => markGuidanceRead(m.id)));
    } catch {
      /* icke-kritisk */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled || !data?.user) return;
      void load(data.user.id);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const close = async (id: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
    await dismissGuidance(id).catch(() => undefined);
  };

  if (messages.length === 0) return null;

  return (
    <div className="space-y-2 py-3">
      {messages.map((m) => (
        <Alert key={m.id}>
          <Lightbulb className="h-4 w-4" />
          <AlertTitle>{m.title}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p className="whitespace-pre-line">{m.body}</p>
            <div className="flex flex-wrap gap-2">
              {resolveGuidanceRoute(m) && (
                <Button size="sm" onClick={() => navigate(resolveGuidanceRoute(m) as string)}>
                  Gå till steget
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => void close(m.id)}>
                Klart
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ))}
    </div>
  );
};

export default GuidanceInbox;
